import { NavigateBackLink } from '@exam-management/components';
import { QuestionGroupStatus } from '@exam-management/graphql';
import {
  GoToParagraphLink,
  QuestionLayout,
  LocationSectionWrapper,
  LocationFields,
  DetailSectionWrapper,
} from '@exam-management/qcontent-management';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import {
  ItemSetSubQuestionEditForm,
  PropertiesFormSection,
  ItemSetSubQuestionEditFormActions,
  useGetQBSLevelAndLevelItemsForTask,
  useSingleQuestionDetails,
  DetailsFormSection,
  useItemSetQuestionDetails,
  ItemSetSubQuestionInformationSection,
  DetailView,
  PropertiesView,
} from '@exam-management/question-creation-task-question-management';
import { useParams } from '@exam-management/router';
import { Flex, Loading, PageHeader, Text, PaperSection, SectionTitle } from '@shared/components';

export default function ItemSetSubQuestionDetailsPage() {
  const { task, roles } = useQuestionCreationTaskDetails();
  const { singleQuestion } = useSingleQuestionDetails();
  const { questionId, subQuestionId } = useParams(
    '/my-task/question-creation/:taskId/item-set/:questionId/:subQuestionId',
  );
  const {
    qbsLevelItems,
    qbsLevels,
    loading: isGettingQBSLevel,
  } = useGetQBSLevelAndLevelItemsForTask(singleQuestion.refQBankStructureId, task.id);

  if (isGettingQBSLevel) return <Loading sx={{ height: '100%' }} />;
  const { itemSet } = useItemSetQuestionDetails();

  const levelItem = qbsLevelItems.filter((item) =>
    itemSet?.questionStructures.find((structure) => structure?.refLevelItemId ?? '' === item.id),
  );

  const isEditable = itemSet.status === QuestionGroupStatus.InDevelopment && roles.isAssignee;

  return (
    <>
      <PageHeader
        title={singleQuestion?.refCode}
        subtitleSection={
          <Flex
            sx={{
              gap: (theme) => theme.spacing(4),
              alignItems: 'center',
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            <Text variant='body2'>Task ID: {task.code}</Text>
            <Text variant='body2'>{task.metadata?.requirement.qBankStructure?.name}</Text>
          </Flex>
        }
        linkAdornment={
          <NavigateBackLink
            to='/my-task/question-creation/:taskId/item-set/:questionId'
            params={{ taskId: task.id, questionId }}
          />
        }
      />
      <ItemSetSubQuestionEditForm
        qbsLevels={qbsLevels}
        qbsLevelItems={qbsLevelItems}
        taskId={task.id}
      >
        <QuestionLayout
          sideContent={
            <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
              <PaperSection title='Location'>
                <LocationSectionWrapper>
                  <LocationFields qbsLevels={qbsLevels} qbsLevelItems={levelItem} />
                </LocationSectionWrapper>
              </PaperSection>
            </Flex>
          }
        >
          <Flex sx={{ width: '100%', flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
            <PaperSection title='Question Details'>
              <DetailSectionWrapper>
                <ItemSetSubQuestionInformationSection
                  mode='workflow'
                  readonly={!isEditable}
                  titleEndAdornment={
                    <GoToParagraphLink
                      to='/my-task/question-creation/:taskId/item-set/:questionId'
                      params={{ taskId: task.id, questionId }}
                    />
                  }
                />
                <SectionTitle title='Question' />
                {isEditable ? (
                  <DetailsFormSection taskId={task.id} questionId={subQuestionId} />
                ) : (
                  <DetailView />
                )}
                <SectionTitle title='Properties' />
                {isEditable ? (
                  <PropertiesFormSection taskId={task.id} questionId={subQuestionId} />
                ) : (
                  <PropertiesView />
                )}
              </DetailSectionWrapper>
            </PaperSection>
            {isEditable ? <ItemSetSubQuestionEditFormActions /> : null}
          </Flex>
        </QuestionLayout>
      </ItemSetSubQuestionEditForm>
    </>
  );
}
